import { useState, useEffect, useRef } from 'react';
import { ImageIcon, Upload, Link, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export function ImageTile({ filter, onConfigChange }) {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isEditing, setIsEditing] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const containerRef = useRef(null);
  const fileInputRef = useRef(null);

  const imageUrl = filter?.imageUrl || '';
  const objectFit = filter?.objectFit || 'cover';

  // Observe container size changes
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const handleUrlSubmit = () => {
    if (urlInput.trim()) {
      onConfigChange?.({ ...filter, imageUrl: urlInput.trim() });
      setIsEditing(false);
      setUrlInput('');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        onConfigChange?.({ ...filter, imageUrl: event.target.result });
        setIsEditing(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClearImage = () => {
    onConfigChange?.({ ...filter, imageUrl: '' });
  };

  const toggleObjectFit = () => {
    const newFit = objectFit === 'cover' ? 'contain' : 'cover';
    onConfigChange?.({ ...filter, objectFit: newFit });
  };

  const { width, height } = dimensions;
  const isSmall = width < 200 || height < 150;

  // No image set - show placeholder/upload UI
  if (!imageUrl) {
    return (
      <div ref={containerRef} className="flex flex-col items-center justify-center h-full p-4">
        {isEditing ? (
          <div className="w-full space-y-3">
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="Paste image URL..."
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleUrlSubmit()}
                className="flex-1 text-sm"
              />
              <Button size="sm" onClick={handleUrlSubmit}>
                Add
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-400">or</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Image
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileSelect}
            />
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-gray-500"
              onClick={() => setIsEditing(false)}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <button
            onClick={() => setIsEditing(true)}
            className="flex flex-col items-center justify-center p-4 rounded-lg border-2 border-dashed border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors w-full h-full"
          >
            <div className="bg-gray-100 p-3 rounded-full mb-2">
              <ImageIcon className="h-6 w-6 text-gray-400" />
            </div>
            {!isSmall && (
              <>
                <p className="text-sm font-medium text-gray-600">Add Image</p>
                <p className="text-xs text-gray-400 mt-1">Click to add URL or upload</p>
              </>
            )}
          </button>
        )}
      </div>
    );
  }

  // Image set - display it
  return (
    <div ref={containerRef} className="relative h-full w-full group">
      <img
        src={imageUrl}
        alt="Dashboard tile"
        className={cn(
          "w-full h-full rounded-md",
          objectFit === 'cover' ? 'object-cover' : 'object-contain'
        )}
        onError={(e) => {
          e.target.style.display = 'none';
        }}
      />

      {/* Overlay controls on hover */}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 pointer-events-none">
        <Button
          size="sm"
          variant="secondary"
          className="h-8 pointer-events-auto"
          onClick={toggleObjectFit}
        >
          {objectFit === 'cover' ? 'Fit' : 'Fill'}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          className="h-8 pointer-events-auto"
          onClick={() => setIsEditing(true)}
        >
          <Link className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant="destructive"
          className="h-8 pointer-events-auto"
          onClick={handleClearImage}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Edit mode overlay */}
      {isEditing && (
        <div className="absolute inset-0 bg-white/95 flex items-center justify-center p-4">
          <div className="w-full space-y-3">
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="Paste new image URL..."
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleUrlSubmit()}
                className="flex-1 text-sm"
                autoFocus
              />
              <Button size="sm" onClick={handleUrlSubmit}>
                Update
              </Button>
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload New Image
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileSelect}
            />
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-gray-500"
              onClick={() => {
                setIsEditing(false);
                setUrlInput('');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
